import React, { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import AnnotationLayer from "./AnnotationLayer";
import {
  addAgendaAttachmentAnnotation,
  addAnnotation,
  listAgendaAttachmentAnnotations,
  listAnnotations,
  shareAnnotation,
} from "../../api/boardbacks.api";
import { canEditGovernance, canViewGovernance } from "../../accessControl";
import { connectDocumentChannel } from "../../api/boardbacks.ws";
import apiClient from "../../api/apiClient";
import { documentRequest, getDocumentBlobResponse } from "../../api/documentDownload";
import Button from "../../ui/Button";
import { useAuth } from "../../useAuth";
import { configurePdfWorker } from "../../pdfWorker";

configurePdfWorker(pdfjs);

const readHeaderValue = (headers, headerName) => {
  if (!headers || !headerName) return "";
  if (typeof headers.get === "function") {
    return headers.get(headerName) || headers.get(headerName.toLowerCase()) || "";
  }
  const directValue =
    headers[headerName] ??
    headers[headerName.toLowerCase()] ??
    headers[headerName.toUpperCase()];
  if (Array.isArray(directValue)) {
    return directValue.join(", ");
  }
  return typeof directValue === "string" ? directValue : "";
};

const decodeBinaryText = async (data) => {
  if (data == null) return "";
  if (typeof data === "string") return data;
  if (data instanceof Blob) {
    if (typeof data.text === "function") {
      return data.text();
    }
    if (typeof data.arrayBuffer === "function") {
      const buffer = await data.arrayBuffer();
      return new TextDecoder().decode(new Uint8Array(buffer));
    }
    if (typeof FileReader !== "undefined") {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
        reader.onerror = () => resolve("");
        reader.readAsText(data);
      });
    }
    return "";
  }
  if (data instanceof ArrayBuffer) {
    return new TextDecoder().decode(new Uint8Array(data));
  }
  if (ArrayBuffer.isView(data)) {
    return new TextDecoder().decode(new Uint8Array(data.buffer, data.byteOffset, data.byteLength));
  }
  try {
    return JSON.stringify(data);
  } catch (_err) {
    return String(data);
  }
};

const parseJsonPayload = async (data) => {
  const text = (await decodeBinaryText(data)).trim();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_err) {
    return null;
  }
};

const readPdfFailure = async ({ status, headers, data, fallback = "Failed to load PDF document", phase = "fetch/auth" }) => {
  if (!status) {
    return `${fallback}: Network or authentication request failed [${phase}].`;
  }
  let detail = "";
  const contentType = readHeaderValue(headers, "content-type") || "";
  const payload = contentType.includes("application/json") ? await parseJsonPayload(data) : null;
  if (payload?.detail) {
    detail = typeof payload.detail === "string" ? payload.detail : JSON.stringify(payload.detail);
  } else {
    const text = await decodeBinaryText(data);
    if (text && !text.trim().startsWith("<")) {
      detail = text.trim();
    }
  }
  if (detail) {
    return `${fallback} (${status}): ${detail} [${phase}].`;
  }
  if (status === 403) {
    return `${fallback} (403): You no longer have access to this document [${phase}].`;
  }
  if (status === 404) {
    return `${fallback} (404): The stored file could not be found [${phase}].`;
  }
  return `${fallback} (${status}) [${phase}].`;
};

const toPdfRenderFailure = (error) => {
  const detail = error?.message?.trim();
  return detail
    ? `Failed to render PDF document: ${detail} [pdf parse/load].`
    : "Failed to render PDF document [pdf parse/load].";
};

const toEmptyPdfFailure = (status, contentType) => {
  const normalizedType = contentType || "unknown content type";
  return `Failed to load PDF document (${status || "unknown"}): Empty PDF response from ${normalizedType} [empty blob].`;
};

const loadPdfBinary = async (url, absoluteBase, hops = 0) => {
  if (hops > 3) throw new Error("Too many document URL responses.");
  const request = documentRequest(url, absoluteBase);
  const response = await getDocumentBlobResponse(request.url, absoluteBase);
  const status = Number(response?.status || 0);
  const contentType = readHeaderValue(response?.headers, "content-type").toLowerCase();

  if (status < 200 || status >= 300) {
    throw new Error(await readPdfFailure({ status, headers: response?.headers, data: response?.data }));
  }

  if (contentType.includes("application/json")) {
    const payload = await parseJsonPayload(response?.data);
    if (payload?.url && request.trusted) {
      return loadPdfBinary(payload.url, absoluteBase, hops + 1);
    }
    throw new Error(
      await readPdfFailure({
        status,
        headers: response?.headers,
        data: response?.data,
        phase: "fetch/auth",
      })
    );
  }

  if (contentType.includes("text/html") || contentType.includes("text/plain")) {
    throw new Error(
      await readPdfFailure({
        status,
        headers: response?.headers,
        data: response?.data,
        fallback: "Failed to load PDF document",
        phase: "fetch/auth",
      })
    );
  }

  const blob =
    response?.data instanceof Blob
      ? response.data
      : new Blob([response?.data ?? new Uint8Array()], {
          type: contentType || "application/pdf",
        });
  if (!blob.size) {
    throw new Error(toEmptyPdfFailure(status, contentType));
  }
  return blob;
};

export default function BoardDocumentViewer({
  documentId,
  attachmentId,
  fileUrl,
  showNotesPanel = true,
  questions = [],
  onCurrentPageChange,
  onQuestionMarkerClick,
}) {
  const auth = useAuth();
  const user = auth?.user;
  const governanceCanView = canViewGovernance(user);
  const governanceCanEdit = canEditGovernance(user);
  const isInlinePackViewer = !showNotesPanel;
  const [numPages, setNumPages] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [draft, setDraft] = useState(null); // {page,x,y,text}
  const [editorCollapsed, setEditorCollapsed] = useState(false);
  const [fileSource, setFileSource] = useState(null);
  const [fileError, setFileError] = useState("");
  const [fileLoading, setFileLoading] = useState(false);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [pageWidth, setPageWidth] = useState(null);
  const [zoomMode, setZoomMode] = useState("fit-page");
  const [zoomScale, setZoomScale] = useState(1);
  const pageContainerRef = useRef(null);
  const renderPdfLayers = showNotesPanel;
  const effectivePageWidth = useMemo(() => {
    if (!pageWidth) return undefined;
    if (zoomMode === "fit-page") return Math.min(pageWidth, 680);
    if (zoomMode === "fit-width") return pageWidth;
    return Math.max(360, Math.round(pageWidth * zoomScale));
  }, [pageWidth, zoomMode, zoomScale]);
  const zoomLabel = zoomMode === "fit-page" ? "Fit page" : zoomMode === "fit-width" ? "Fit width" : `${Math.round(zoomScale * 100)}%`;
  const questionMarkersByPage = useMemo(() => {
    const markerMap = new Map();
    (Array.isArray(questions) ? questions : []).forEach((question) => {
      const pageNumber = Number(question?.page_number || 0);
      if (!pageNumber) return;
      const pageQuestions = markerMap.get(pageNumber) || [];
      pageQuestions.push(question);
      markerMap.set(pageNumber, pageQuestions);
    });
    return markerMap;
  }, [questions]);

  useEffect(() => {
    let active = true;
    let objectUrl = null;
    setFileError("");
    setFileSource(null);
    setNumPages(null);
    setDocumentLoaded(false);

    setFileLoading(true);
    const base = apiClient.defaults.baseURL || "";
    const normalizedBase = base && base.endsWith("/") ? base.slice(0, -1) : base;
    const absoluteBase = normalizedBase.startsWith("http")
      ? normalizedBase
      : `${window.location.origin}${normalizedBase}`;
    const fetchUrl = fileUrl || (documentId ? `/boardbacks/documents/${documentId}/file` : null);

    if (!fetchUrl) {
      setFileLoading(false);
      return () => {
        active = false;
      };
    }

    loadPdfBinary(fetchUrl, absoluteBase)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setFileSource(objectUrl);
      })
      .catch((error) => {
        if (active) {
          const message = error?.message?.trim();
          setFileError(message || "Failed to load PDF document: Network or authentication request failed [fetch/auth].");
        }
      })
      .finally(() => {
        if (active) setFileLoading(false);
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [documentId, fileUrl]);

  const setManualZoom = (nextScale) => {
    setZoomMode("manual");
    setZoomScale(Math.max(0.6, Math.min(1.6, Number(nextScale.toFixed(2)))));
  };

  useEffect(() => {
    const targetId = attachmentId || documentId;
    if (!targetId) return;
    const listFn = attachmentId ? listAgendaAttachmentAnnotations : listAnnotations;
    listFn(targetId).then(setAnnotations).catch(() => setAnnotations([]));
    if (attachmentId) return () => {};
    const ws = connectDocumentChannel(documentId, (msg) => {
      if (msg.visibility === "shared" && msg.document_id === documentId) {
        setAnnotations((prev) => [...prev, msg]);
      }
    });
    return () => ws && ws.close();
  }, [documentId, attachmentId]);

  useEffect(() => {
    if (!pageContainerRef.current) return;
    const node = pageContainerRef.current;
    const updateWidth = () => {
      const rect = node.getBoundingClientRect();
      if (rect.width) {
        const nextWidth = isInlinePackViewer ? Math.min(Math.floor(rect.width), 960) : Math.floor(rect.width);
        setPageWidth(nextWidth);
      }
    };
    updateWidth();
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateWidth);
      observer.observe(node);
    } else {
      window.addEventListener("resize", updateWidth);
    }
    return () => {
      if (observer) {
        observer.disconnect();
      } else {
        window.removeEventListener("resize", updateWidth);
      }
    };
  }, [fileSource, isInlinePackViewer]);

  const handleClick = (e, pageNumber) => {
    if (!showNotesPanel || !governanceCanView) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setDraft((prev) => ({
      page: pageNumber,
      x,
      y,
      text: prev?.text || "",
    }));
    setEditorCollapsed(false);
  };

  const saveAnnotation = async (visibility) => {
    if (!governanceCanView) return;
    if (visibility === "shared" && !governanceCanEdit) return;
    if (!draft || draft.page == null) return;
    const createFn = attachmentId ? addAgendaAttachmentAnnotation : addAnnotation;
    const targetId = attachmentId || documentId;
    if (!targetId) return;
    const created = await createFn(targetId, {
      page: draft.page,
      x: draft.x,
      y: draft.y,
      body: draft.text || "Note",
      visibility,
    });
    if (visibility === "shared") {
      await shareAnnotation(created.id);
    }
    setAnnotations((prev) => [...prev, { ...created, visibility }]);
    setDraft(null);
    setEditorCollapsed(false);
  };

  const startDraft = () => {
    setDraft({ page: null, x: null, y: null, text: "" });
    setEditorCollapsed(false);
  };

  const cancelDraft = () => {
    setDraft(null);
    setEditorCollapsed(false);
  };

  const hasAnchor = draft?.page != null;
  const handleDocumentLoadError = (error) => {
    setDocumentLoaded(false);
    setNumPages(null);
    setFileError(toPdfRenderFailure(error));
  };
  const handleDocumentLoadSuccess = ({ numPages: pages }) => {
    setDocumentLoaded(true);
    setNumPages(pages);
    if (pages) {
      onCurrentPageChange?.(1);
    }
    if (!pages) {
      setFileError("Failed to render PDF document: Loaded PDF contains zero pages [zero-page render].");
    }
  };

  return (
    <div
      className="board-doc-viewer"
      style={{
        display: showNotesPanel ? "flex" : "block",
        gap: showNotesPanel ? 16 : 0,
        alignItems: "flex-start",
        flexWrap: "nowrap",
      }}
    >
      <div
        ref={pageContainerRef}
        style={{
          flex: "1 1 0",
          minWidth: 0,
          width: "100%",
          display: "grid",
          gap: isInlinePackViewer ? 18 : 0,
          justifyItems: isInlinePackViewer ? "center" : "stretch",
          alignContent: "start",
        }}
      >
        {fileLoading && <div style={{ padding: 12, color: "#6b7280" }}>Loading document...</div>}
        {!fileLoading && fileError && <div style={{ padding: 12, color: "#b91c1c" }}>{fileError}</div>}
        {!fileLoading && !fileError && fileSource && (
          <>
          <div aria-label="PDF zoom controls" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, flexWrap: "wrap", padding: "4px 0" }}>
            <Button variant="secondary" onClick={() => setManualZoom(zoomMode === "manual" ? zoomScale - 0.1 : 0.9)} style={{ minHeight: 30, padding: "4px 8px" }}>Zoom out</Button>
            <span aria-label="Current PDF zoom" style={{ minWidth: 64, textAlign: "center", fontSize: 12, color: "#475569", fontWeight: 700 }}>{zoomLabel}</span>
            <Button variant="secondary" onClick={() => setManualZoom(zoomMode === "manual" ? zoomScale + 0.1 : 1.1)} style={{ minHeight: 30, padding: "4px 8px" }}>Zoom in</Button>
            <Button variant="secondary" onClick={() => setZoomMode("fit-page")} style={{ minHeight: 30, padding: "4px 8px" }}>Fit page</Button>
            <Button variant="secondary" onClick={() => setZoomMode("fit-width")} style={{ minHeight: 30, padding: "4px 8px" }}>Fit width</Button>
          </div>
          <Document
            file={fileSource}
            onLoadSuccess={handleDocumentLoadSuccess}
            onLoadError={handleDocumentLoadError}
            onSourceError={handleDocumentLoadError}
          >
            {Array.from(new Array(numPages || 0), (el, index) => {
              const pageNumber = index + 1;
              const pageQuestions = questionMarkersByPage.get(pageNumber) || [];
              return (
                <div
                  key={`page_${pageNumber}`}
                  style={{
                    position: "relative",
                    marginBottom: isInlinePackViewer ? 18 : 12,
                    background: "#ffffff",
                    borderRadius: isInlinePackViewer ? 14 : 0,
                    boxShadow: isInlinePackViewer ? "0 18px 34px rgba(15,23,42,0.12)" : "none",
                    overflow: "hidden",
                  }}
                  onMouseEnter={() => onCurrentPageChange?.(pageNumber)}
                  onClick={(e) => {
                    onCurrentPageChange?.(pageNumber);
                    handleClick(e, pageNumber);
                  }}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={effectivePageWidth}
                    renderTextLayer={renderPdfLayers}
                    renderAnnotationLayer={renderPdfLayers}
                  />
                  <AnnotationLayer annotations={annotations.filter((a) => a.page === pageNumber)} />
                  {pageQuestions.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onQuestionMarkerClick?.(pageNumber, pageQuestions);
                      }}
                      title={`${pageQuestions.length} question${pageQuestions.length === 1 ? "" : "s"} on page ${pageNumber}`}
                      style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        border: "1px solid rgba(13,148,136,0.28)",
                        background: "rgba(240,253,250,0.94)",
                        color: "#0f766e",
                        borderRadius: 999,
                        minWidth: 26,
                        height: 26,
                        padding: "0 8px",
                        fontSize: 11,
                        fontWeight: 700,
                        boxShadow: "0 6px 12px rgba(15,23,42,0.08)",
                        cursor: "pointer",
                      }}
                    >
                      Q{pageQuestions.length}
                    </button>
                  )}
                </div>
              );
            })}
          </Document>
          </>
        )}
        {!fileLoading && !fileError && fileSource && documentLoaded && !numPages && (
          <div style={{ padding: 12, color: "#b91c1c" }}>
            Failed to render PDF document: Loaded PDF contains zero pages [zero-page render].
          </div>
        )}
      </div>
      {showNotesPanel && (
        <div
          style={{
            flex: "0 1 320px",
            minWidth: 260,
            position: "sticky",
            top: 12,
            alignSelf: "flex-start",
          }}
        >
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: 12,
              background: "#fff",
              boxShadow: "0 10px 20px rgba(15,23,42,0.08)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontWeight: 600 }}>Notes</div>
              <Button
                onClick={() => setEditorCollapsed((prev) => !prev)}
                variant="secondary"
                style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "4px 8px" }}
              >
                {editorCollapsed ? "Expand" : "Minimize"}
              </Button>
            </div>
            <Button
              onClick={startDraft}
              disabled={Boolean(draft) || !governanceCanView}
              variant="primary"
              style={{
                width: "100%",
                border: "1px solid #111827",
                background: draft || !governanceCanView ? "#f3f4f6" : "#111827",
                color: draft || !governanceCanView ? "#6b7280" : "#fff",
                borderRadius: 8,
                padding: "8px 10px",
                cursor: draft || !governanceCanView ? "not-allowed" : "pointer",
              }}
            >
              Add note
            </Button>
            {!draft && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                Click &quot;Add note&quot;, then click on the document to place the marker.
              </div>
            )}
            {draft && editorCollapsed && (
              <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Draft note minimized.</div>
                <Button
                  onClick={cancelDraft}
                  variant="secondary"
                  style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "4px 8px" }}
                >
                  Cancel
                </Button>
              </div>
            )}
            {draft && !editorCollapsed && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
                  {hasAnchor ? `Anchored on page ${draft.page}.` : "Click on the document to place the marker."}
                </div>
                <textarea
                  value={draft.text}
                  onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                  rows={4}
                  style={{ width: "100%", borderRadius: 8, borderColor: "#d1d5db", padding: 8 }}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <Button
                    onClick={() => saveAnnotation("private")}
                    disabled={!hasAnchor || !governanceCanView}
                    variant="secondary"
                  >
                    Save private
                  </Button>
                  <Button
                    onClick={() => saveAnnotation("shared")}
                    disabled={!hasAnchor || !governanceCanEdit}
                    variant="primary"
                  >
                    Save & share
                  </Button>
                  <Button onClick={cancelDraft} variant="secondary">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
